// src/utils/invoiceGenerator.js
// Generación e impresión del invoice en PDF/print — usado por ClaimStatus,
// ClaimsList y ClaimsDirectory para no duplicar el mismo HTML tres veces.
import { formatDateToMMDDYYYY } from './dateHelpers';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value || 0);
};

// Los campos del reclamo (nombre del cliente, causa, etc.) se escriben tal
// cual en un string de HTML que luego se pasa a document.write — sin
// escapar, un nombre de cliente con "<" o "&" rompía el layout del invoice,
// y en teoría cualquier dato que llegara con HTML/script se ejecutaría.
const escapeHtml = (value) => {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const LOGO_URL = 'https://i.imgur.com/DbVoxCH.png';

// Estilos y encabezado del invoice, compartidos por el invoice de cheque y
// la factura personalizada del operador (mismo formato).
const INVOICE_STYLES = `
        * { margin: 0; padding: 0; box-sizing: border-box; }

        body {
          font-family: 'Rubik', 'Helvetica Neue', Arial, sans-serif;
          background: #ffffff;
          padding: 40px;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
        }

        .invoice-container {
          max-width: 750px;
          width: 100%;
          background: white;
          padding: 45px 50px;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
        }

        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 25px;
          padding-bottom: 20px;
          border-bottom: 1px solid #d0d0d0;
        }

        .header-left {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: left;
          justify-content: center;
          padding-top: 10px;
        }

        .header-left .company-name {
          font-family: 'Rubik', sans-serif;
          font-size: 22px;
          font-weight: 700;
          color: #1a1a2e;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .header-left .company-info {
          font-family: 'Rubik', sans-serif;
          font-size: 13px;
          font-weight: 400;
          color: #666;
          line-height: 1.7;
        }

        .header-right {
          display: flex;
          justify-content: flex-end;
          align-items: flex-start;
        }

        .header-right img {
          max-width: 200px;
          height: auto;
          display: block;
        }

        .invoice-title-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 25px;
          padding-bottom: 18px;
          border-bottom: 1px solid #e0e0e0;
        }

        .invoice-title {
          text-align: left;
        }

        .invoice-title h1 {
          font-family: 'Rubik', sans-serif;
          font-size: 28px;
          font-weight: 700;
          color: #0066ff;
          letter-spacing: 3px;
          margin-bottom: 6px;
        }

        .invoice-title .invoice-number {
          font-family: 'Rubik', sans-serif;
          font-size: 15px;
          font-weight: 500;
          color: #666;
        }

        .info-dates {
          display: flex;
          gap: 50px;
        }

        .info-box { text-align: right; }

        .info-label {
          font-family: 'Rubik', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 4px;
        }

        .info-value {
          font-family: 'Rubik', sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: #1a1a2e;
        }

        .client-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 22px;
          padding-bottom: 18px;
          border-bottom: 1px solid #e0e0e0;
        }

        .client-box { flex: 1; }

        .client-label {
          font-family: 'Rubik', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 5px;
        }

        .client-name {
          font-family: 'Rubik', sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: #1a1a2e;
          margin-bottom: 3px;
        }

        .client-detail {
          font-family: 'Rubik', sans-serif;
          font-size: 12px;
          font-weight: 400;
          color: #888;
        }

        .check-section {
          margin-bottom: 22px;
          padding-bottom: 18px;
          border-bottom: 1px solid #e0e0e0;
        }

        .section-title {
          font-family: 'Rubik', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #1a1a2e;
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .check-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .check-label {
          font-family: 'Rubik', sans-serif;
          font-size: 12px;
          font-weight: 400;
          color: #888;
        }

        .check-value {
          font-family: 'Rubik', sans-serif;
          font-size: 12px;
          font-weight: 600;
          color: #1a1a2e;
        }

        .amount-section {
          margin-bottom: 22px;
          padding-bottom: 18px;
          border-bottom: 1px solid #e0e0e0;
        }

        .amount-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          padding: 3px 0;
        }

        .amount-label {
          font-family: 'Rubik', sans-serif;
          font-size: 12px;
          font-weight: 400;
          color: #888;
        }

        .amount-value {
          font-family: 'Rubik', sans-serif;
          font-size: 12px;
          font-weight: 600;
          color: #1a1a2e;
        }

        .amount-row.highlight {
          margin-top: 8px;
          padding-top: 10px;
          border-top: 2px solid #0066ff;
        }

        .amount-row.highlight .amount-label,
        .amount-row.highlight .amount-value {
          font-family: 'Rubik', sans-serif;
          font-size: 14px;
          font-weight: 700;
          color: #0066ff;
        }

        .notes-section {
          margin: 18px 0 22px 0;
          padding: 14px 18px;
          background: #f8f9fa;
          border-radius: 4px;
          border-left: 4px solid #0066ff;
        }

        .notes-title {
          font-family: 'Rubik', sans-serif;
          font-size: 11px;
          font-weight: 600;
          color: #0066ff;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 5px;
        }

        .notes-text {
          font-family: 'Rubik', sans-serif;
          font-size: 12px;
          font-weight: 400;
          color: #666;
          line-height: 1.6;
        }

        .total-section {
          display: flex;
          justify-content: flex-end;
          margin-top: 10px;
          padding-top: 18px;
          border-top: 2px solid #0066ff;
        }

        .total-box { text-align: right; }

        .total-label {
          font-family: 'Rubik', sans-serif;
          font-size: 14px;
          font-weight: 600;
          color: #1a1a2e;
          margin-bottom: 3px;
        }

        .total-amount {
          font-family: 'Rubik', sans-serif;
          font-size: 26px;
          font-weight: 700;
          color: #0066ff;
        }

        @media print {
          body { background: white; padding: 20px; margin: 0; }
          .invoice-container { border: none; padding: 25px 30px; max-width: 100%; box-shadow: none; }
          .invoice-container, .invoice-container * { page-break-inside: avoid; }
        }

        @media (max-width: 600px) {
          body { padding: 20px; }
          .invoice-container { padding: 25px 20px; }
          .invoice-header { flex-direction: column-reverse; align-items: flex-start; gap: 20px; }
          .header-right img { max-width: 150px; }
          .invoice-title-row { flex-direction: column; align-items: flex-start; gap: 15px; }
          .info-dates { width: 100%; justify-content: space-between; }
          .info-box { text-align: left; }
          .client-section { flex-direction: column; gap: 15px; }
        }
`;

const INVOICE_HEADER = `        <div class="invoice-header">
          <div class="header-left">
            <div class="company-name">CSR CLAIMS ASSISTANT LLC</div>
            <div class="company-info">www.csrclaimsassistant.com</div>
            <div class="company-info">info@csrclaimsassistant.com</div>
            <div class="company-info">(407) 279-0703</div>
          </div>
          <div class="header-right">
            <img
              src="${LOGO_URL}"
              alt="CSR Claims Assistant"
              onerror="this.onerror=null; this.style.display='none';"
            />
          </div>
        </div>`;

export const generateInvoiceHTML = (claim) => {
  const totalCheckAmount = parseFloat(claim.amount) || 0;
  const adjusterPercentage = parseFloat(claim.adjusterPercentage) || 0;
  const csrPercentage = parseFloat(claim.csrPercentage) || 10;

  const totalToCollect = (totalCheckAmount * adjusterPercentage) / 100;
  const invoiceAmount = (totalToCollect * csrPercentage) / 100;

  const checkDateFormatted = formatDateToMMDDYYYY(claim.checkDate);

  const today = new Date();
  const todayFormatted = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Invoice ${escapeHtml(claim.invoiceNumber)}</title>
      <link href="https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700&display=swap" rel="stylesheet">
      <style>${INVOICE_STYLES}</style>
    </head>
    <body>
      <div class="invoice-container">

${INVOICE_HEADER}
        <div class="invoice-title-row">
          <div class="invoice-title">
            <h1>INVOICE</h1>
            <div class="invoice-number">INV-${escapeHtml(claim.invoiceNumber)}</div>
          </div>
          <div class="info-dates">
            <div class="info-box">
              <div class="info-label">DATE</div>
              <div class="info-value">${todayFormatted}</div>
            </div>
          </div>
        </div>

        <div class="client-section">
          <div class="client-box">
            <div class="client-label">BILL TO</div>
            <div class="client-name">${escapeHtml(claim.companyName) || 'N/A'}</div>
          </div>
          <div class="client-box">
            <div class="client-label">PAY TO</div>
            <div class="client-name">CSR Claims Assistant LLC</div>
          </div>
        </div>

        <div class="client-section">
          <div class="client-box">
            <div class="client-label">CLIENT DESCRIPTION</div>
            <div class="client-name">${escapeHtml(claim.clientName) || 'N/A'}</div>
            <div class="client-detail">Cause of Loss: ${escapeHtml(claim.cause) || 'N/A'}</div>
          </div>
          <div class="client-box">
            <div class="client-label">DESIGNATED ADJUSTER</div>
            <div class="client-name">${escapeHtml(claim.adjusterName) || 'N/A'}</div>
            <div class="client-detail">Claim Number: ${escapeHtml(claim.claimNumber) || 'N/A'}</div>
          </div>
        </div>

        <div class="check-section">
          <div class="section-title">Check Information</div>
          <div class="check-row">
            <span class="check-label">Check Issue Date</span>
            <span class="check-value">${checkDateFormatted}</span>
          </div>
          <div class="check-row">
            <span class="check-label">Total Check Amount</span>
            <span class="check-value">${formatCurrency(totalCheckAmount)}</span>
          </div>
          <div class="check-row">
            <span class="check-label">Portion to be charged</span>
            <span class="check-value">${formatCurrency(totalCheckAmount)}</span>
          </div>
          <div class="check-row">
            <span class="check-label">Percentage of the portion over the total amount</span>
            <span class="check-value">${adjusterPercentage}%</span>
          </div>
          <div class="check-row">
            <span class="check-label">Total to collect</span>
            <span class="check-value">${formatCurrency(totalToCollect)}</span>
          </div>
        </div>

        <div class="amount-section">
          <div class="amount-row">
            <span class="amount-label">Invoice Amount</span>
            <span class="amount-value">${formatCurrency(totalToCollect)}</span>
          </div>
          <div class="amount-row">
            <span class="amount-label">Total amount to be discounted</span>
            <span class="amount-value">${formatCurrency(invoiceAmount)}</span>
          </div>
          <div class="amount-row">
            <span class="amount-label">Percent</span>
            <span class="amount-value">${csrPercentage}%</span>
          </div>
          <div class="amount-row">
            <span class="amount-label">Amount</span>
            <span class="amount-value">${formatCurrency(totalToCollect)}</span>
          </div>

        <div class="total-section">
          <div class="total-box">
            <div class="total-label">TOTAL</div>
            <div class="total-amount">${formatCurrency(invoiceAmount)}</div>
          </div>
        </div>

      </div>
    </body>
    </html>
  `;
};

// Se abre como Blob URL en una pestaña: el invoice queda visible aunque se
// cierre el diálogo de impresión (antes, document.write en un popup en blanco
// dejaba la ventana vacía/bloqueada y no había forma de volver a verlo).
// `win` permite pasar una pestaña ya abierta en el clic (antes de esperar a
// Firestore), porque el navegador bloquea las que se abren después.
export const viewInvoice = (claim, { print = false, win: openedWin = null } = {}) => {
  const invoiceHTML = generateInvoiceHTML(claim);
  const url = URL.createObjectURL(new Blob([invoiceHTML], { type: 'text/html;charset=utf-8' }));
  let win = openedWin;
  if (win) win.location.href = url;
  else win = window.open(url, '_blank');

  if (!win) {
    URL.revokeObjectURL(url);
    alert('Please allow popups to view the invoice.');
    return;
  }

  if (print) {
    win.addEventListener('load', () => setTimeout(() => win.print(), 500));
  }
  setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
};

export const printInvoice = (claim) => viewInvoice(claim, { print: true });

// Facturas con líneas de servicio, con el mismo formato del invoice:
//   cliente  — CSR le factura a una empresa de ajustadores (encabezado de CSR).
//   operador — el operador le cobra a CSR (personalizada o de contrato).
const OPERATOR_HEADER = `        <div class="invoice-header">
          <div class="header-left">
            <div class="company-name">OPERATOR</div>
            <div class="company-info">Services billed to CSR Claims Assistant LLC</div>
          </div>
        </div>`;

const generateServicesInvoiceHTML = ({ header, invoiceNumber, date, billTo, billToDetail, payTo, items, amount, notes }) => {
  const lines = items?.length ? items : [{ service: 'Service', amount }];
  const rows = lines.map(item => `
          <div class="amount-row">
            <span class="amount-label">${escapeHtml(item.service)}</span>
            <span class="amount-value">${formatCurrency(item.amount)}</span>
          </div>`).join('');
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Invoice ${escapeHtml(invoiceNumber)}</title>
      <link href="https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700&display=swap" rel="stylesheet">
      <style>${INVOICE_STYLES}</style>
    </head>
    <body>
      <div class="invoice-container">
${header}

        <div class="invoice-title-row">
          <div class="invoice-title">
            <h1>INVOICE</h1>
            <div class="invoice-number">INV-${escapeHtml(invoiceNumber)}</div>
          </div>
          <div class="info-dates">
            <div class="info-box">
              <div class="info-label">DATE</div>
              <div class="info-value">${escapeHtml(formatDateToMMDDYYYY(date))}</div>
            </div>
          </div>
        </div>

        <div class="client-section">
          <div class="client-box">
            <div class="client-label">BILL TO</div>
            <div class="client-name">${escapeHtml(billTo) || 'N/A'}</div>${billToDetail ? `
            <div class="client-detail">${escapeHtml(billToDetail)}</div>` : ''}
          </div>
          <div class="client-box">
            <div class="client-label">PAY TO</div>
            <div class="client-name">${escapeHtml(payTo)}</div>
          </div>
        </div>

        <div class="amount-section">
          <div class="section-title">Services</div>${rows}
        </div>
${notes ? `
        <div class="notes-section">
          <div class="notes-title">Notes</div>
          <div class="notes-text">${escapeHtml(notes)}</div>
        </div>` : ''}
        <div class="total-section">
          <div class="total-box">
            <div class="total-label">TOTAL</div>
            <div class="total-amount">${formatCurrency(amount)}</div>
          </div>
        </div>

      </div>
    </body>
    </html>
  `;
};

export const generateOperatorInvoiceHTML = (debt) => generateServicesInvoiceHTML({
  header: OPERATOR_HEADER, invoiceNumber: debt.invoiceNumber, date: debt.checkDate,
  billTo: 'CSR Claims Assistant LLC', payTo: 'Operator', items: debt.items, amount: debt.amount, notes: debt.notes
});

export const generateClientInvoiceHTML = (invoice) => generateServicesInvoiceHTML({
  header: INVOICE_HEADER, invoiceNumber: invoice.invoiceNumber, date: invoice.date,
  billTo: invoice.companyName, billToDetail: invoice.adjusterName ? `Adjuster: ${invoice.adjusterName}` : '',
  payTo: 'CSR Claims Assistant LLC', items: invoice.items, amount: invoice.amount, notes: invoice.notes
});

// Con print: true abre el diálogo de impresión para guardarla como PDF,
// igual que la descarga del invoice de cheque.
const openInvoiceHtml = (html, { print = false } = {}) => {
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
  const win = window.open(url, '_blank');
  if (!win) {
    URL.revokeObjectURL(url);
    alert('Please allow popups to view the invoice.');
    return;
  }
  if (print) {
    win.addEventListener('load', () => setTimeout(() => win.print(), 500));
  }
  setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
};

export const viewOperatorInvoice = (debt, options) => openInvoiceHtml(generateOperatorInvoiceHTML(debt), options);
export const viewClientInvoice = (invoice, options) => openInvoiceHtml(generateClientInvoiceHTML(invoice), options);
